import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Plane, Hotel, UtensilsCrossed, Car, ExternalLink, MapPin, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POPULAR_DESTINATIONS = [
  { city: 'Oklahoma City', state: 'OK', label: 'Oklahoma City, OK', desc: 'Home of Surgery Center of Oklahoma — national leader in cash pricing' },
  { city: 'Dallas', state: 'TX', label: 'Dallas, TX', desc: 'Major medical hub with competitive cash rates' },
  { city: 'Nashville', state: 'TN', label: 'Nashville, TN', desc: 'Award-winning surgical centers with bariatric expertise' },
  { city: 'Phoenix', state: 'AZ', label: 'Phoenix, AZ', desc: 'Sun Belt medical tourism destination with top eye centers' },
];

export default function TravelPage() {
  const [searchParams] = useSearchParams();
  const [originZip, setOriginZip] = useState('');
  const [destState, setDestState] = useState(searchParams.get('destination_state') || '');
  const [destCity, setDestCity] = useState(searchParams.get('destination_city') || '');
  const [nights, setNights] = useState(3);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (destState && destCity && originZip) handleEstimate();
  }, []);

  const handleEstimate = async (e) => {
    if (e) e.preventDefault();
    if (!originZip || !destCity || !destState) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/travel/estimate`, {
        origin_zip: originZip,
        destination_state: destState,
        destination_city: destCity,
        nights
      });
      setResult(res.data);
    } catch (e) {
      console.error('Travel estimate failed', e);
    } finally {
      setLoading(false);
    }
  };

  const setDestination = (dest) => {
    setDestCity(dest.city);
    setDestState(dest.state);
  };

  return (
    <div className="min-h-screen bg-[#F0EEE9]">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-3">Travel Estimator</p>
          <h1 className="text-4xl font-medium text-stone-900 mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Plan Your Medical Journey</h1>
          <p className="text-stone-600 max-w-2xl">Calculate travel, lodging, and meal costs to compare your all-in total against your local insurance out-of-pocket.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calculator */}
          <div className="lg:col-span-2">
            {/* Popular Destinations */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-stone-900 mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Top Cash-Pay Destinations</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {POPULAR_DESTINATIONS.map((dest, i) => (
                  <button
                    key={i}
                    onClick={() => setDestination(dest)}
                    className={`text-left p-4 border transition-colors card-hover ${
                      destCity === dest.city ? 'bg-[#F0EEE9] border-[#0D3D4A]' : 'bg-white border-stone-200'
                    }`}
                    data-testid={`destination-${dest.city.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin size={14} className="text-[#0D3D4A]" />
                      <span className="font-semibold text-stone-900 text-sm">{dest.label}</span>
                    </div>
                    <p className="text-xs text-stone-500">{dest.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleEstimate} className="bg-white border border-stone-200 p-8">
              <h2 className="text-lg font-semibold text-stone-900 mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>Travel Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Your ZIP Code (Origin)</label>
                  <input
                    type="text"
                    value={originZip}
                    onChange={e => setOriginZip(e.target.value)}
                    placeholder="e.g. 10001"
                    maxLength={5}
                    className="w-full px-4 py-3 border border-[#A8B4B8] bg-white text-sm text-stone-900 focus:outline-none focus:border-[#0D3D4A]"
                    data-testid="origin-zip-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Destination City</label>
                  <input
                    type="text"
                    value={destCity}
                    onChange={e => setDestCity(e.target.value)}
                    placeholder="e.g. Oklahoma City"
                    className="w-full px-4 py-3 border border-[#A8B4B8] bg-white text-sm text-stone-900 focus:outline-none focus:border-[#0D3D4A]"
                    data-testid="destination-city-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Destination State (2-letter code)</label>
                  <input
                    type="text"
                    value={destState}
                    onChange={e => setDestState(e.target.value)}
                    placeholder="e.g. OK"
                    maxLength={2}
                    className="w-full px-4 py-3 border border-[#A8B4B8] bg-white text-sm text-stone-900 focus:outline-none focus:border-[#0D3D4A] uppercase"
                    data-testid="destination-state-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Nights Stay: <span className="text-[#0D3D4A]">{nights}</span>
                  </label>
                  <input
                    type="range"
                    min={1}
                    max={14}
                    value={nights}
                    onChange={e => setNights(Number(e.target.value))}
                    className="w-full accent-[#0D3D4A] mt-2"
                    data-testid="nights-slider"
                  />
                  <div className="flex justify-between text-xs text-[#A8B4B8] mt-1"><span>1 night</span><span>14 nights</span></div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="bg-[#0D3D4A] text-white px-8 py-4 text-sm font-semibold hover:bg-[#0E4557] transition-colors disabled:opacity-50 flex items-center gap-2"
                data-testid="estimate-travel-submit"
              >
                {loading ? 'Estimating...' : 'Get Travel Estimate'} <ArrowRight size={16} />
              </button>
            </form>
          </div>

          {/* Results */}
          <div>
            {result ? (
              <div className="space-y-4 animate-fade-in" data-testid="travel-result">
                <div className="bg-[#0D3D4A] text-white p-6">
                  <p className="text-[#F0EEE9]/70 text-xs uppercase tracking-wide font-semibold mb-2">Total Trip Estimate</p>
                  <p className="text-xs text-[#F0EEE9]/90 mb-4">{result.destination?.city}, {result.destination?.state} &bull; {result.nights} nights</p>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#F0EEE9]/70">Budget</span>
                      <span className="font-bold">${result.total_estimate?.min?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#F0EEE9]/70">Average</span>
                      <span className="font-bold text-xl">${result.total_estimate?.avg?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#F0EEE9]/70">Comfort</span>
                      <span className="font-bold">${result.total_estimate?.max?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-stone-200 p-6">
                  <h3 className="font-semibold text-stone-900 mb-4 text-sm" style={{fontFamily: 'Outfit, sans-serif'}}>Cost Breakdown</h3>
                  <div className="space-y-3">
                    {[
                      { icon: Plane, label: 'Round-trip flights', value: `$${result.flight?.avg?.toLocaleString()}` },
                      { icon: Hotel, label: `Hotel (${result.nights} nights)`, value: `$${result.hotel?.total?.toLocaleString()}` },
                      { icon: UtensilsCrossed, label: 'Meals', value: `$${result.meals?.toLocaleString()}` },
                      { icon: Car, label: 'Local transport', value: `$${result.local_transport?.toLocaleString()}` },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 text-stone-600">
                          <item.icon size={14} className="text-[#A8B4B8]" />
                          <span>{item.label}</span>
                        </div>
                        <span className="font-medium text-stone-900">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Booking Links */}
                <div className="bg-white border border-stone-200 p-6">
                  <h3 className="font-semibold text-stone-900 mb-4 text-sm" style={{fontFamily: 'Outfit, sans-serif'}}>Book Your Travel</h3>
                  <div className="space-y-2">
                    <a
                      href={result.affiliate_links?.flights?.expedia}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between p-3 border border-stone-200 hover:bg-[#F0EEE9] transition-colors group"
                      data-testid="book-flights-expedia"
                    >
                      <span className="text-sm font-medium text-stone-700">Flights — Expedia</span>
                      <ExternalLink size={14} className="text-[#A8B4B8] group-hover:text-[#0D3D4A]" />
                    </a>
                    <a
                      href={result.affiliate_links?.flights?.kayak}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between p-3 border border-stone-200 hover:bg-[#F0EEE9] transition-colors group"
                      data-testid="book-flights-kayak"
                    >
                      <span className="text-sm font-medium text-stone-700">Flights — Kayak</span>
                      <ExternalLink size={14} className="text-[#A8B4B8] group-hover:text-[#0D3D4A]" />
                    </a>
                    <a
                      href={result.affiliate_links?.hotels?.expedia}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between p-3 border border-stone-200 hover:bg-[#F0EEE9] transition-colors group"
                      data-testid="book-hotels-expedia"
                    >
                      <span className="text-sm font-medium text-stone-700">Hotels — Expedia</span>
                      <ExternalLink size={14} className="text-[#A8B4B8] group-hover:text-[#0D3D4A]" />
                    </a>
                    <a
                      href={result.affiliate_links?.hotels?.booking}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between p-3 border border-stone-200 hover:bg-[#F0EEE9] transition-colors group"
                      data-testid="book-hotels-booking"
                    >
                      <span className="text-sm font-medium text-stone-700">Hotels — Booking.com</span>
                      <ExternalLink size={14} className="text-[#A8B4B8] group-hover:text-[#0D3D4A]" />
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white border border-stone-200 p-10 text-center">
                <Plane size={40} className="text-stone-200 mx-auto mb-4" />
                <p className="text-[#A8B4B8] font-medium mb-2">Enter trip details to see estimates</p>
                <p className="text-[#A8B4B8] text-sm">Select a destination above or enter details manually.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
